"use client"

import * as React from 'react'
import { z } from 'zod'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import type { CrudCustomFieldRenderProps, CrudField, CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import {
  complianceDocumentTypeValues,
  holderTypeValues,
  insuredSumPerValues,
  territorialScopeValues,
  type ComplianceDocumentType,
  type HolderType,
  type InsuredSumPer,
  type TerritorialScope,
} from '../lib/complianceDocument'

export type ComplianceDocumentFormValues = {
  participantProfileId: string
  documentType: ComplianceDocumentType
  documentNumber: string
  documentName?: string
  holderType: HolderType
  issuerName?: string
  issuerCountry?: string
  issuedDate?: string
  validFrom: string
  validUntil?: string
  isIndefinite?: boolean
  reviewIntervalMonths?: number
  nextReviewDate?: string
  insuredSum?: number
  insuredSumCurrency?: string
  insuredSumPer?: InsuredSumPer
  territorialScope?: TerritorialScope
  insurerName?: string
  insurerPolicyNumber?: string
  deductibleAmount?: number
  deductibleCurrency?: string
  adrClassesText?: string
  dgsaAdvisorName?: string
  dgsaCertificateNumber?: string
  alertSuppressed?: boolean
  notes?: string
} & Record<string, unknown>

type ParticipantOption = {
  value: string
  label: string
}

type ParticipantSelectLabels = {
  placeholder: string
  loadingLabel: string
  emptyLabel: string
  errorLoad: string
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableString(value: unknown): string | null {
  return normalizeString(value) ?? null
}

function normalizeOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function normalizeNullableNumber(value: unknown): number | null {
  return normalizeOptionalNumber(value) ?? null
}

function normalizeDateInput(value: unknown): string | undefined {
  const str = normalizeString(value)
  if (!str) return undefined
  if (str.length >= 10) return str.slice(0, 10)
  return str
}

function normalizeDateIso(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const str = value.trim()
    if (!str) return undefined
    if (str.length >= 10) return str.slice(0, 10)
    return str
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  return undefined
}

function parseAdrClassesText(value: unknown): string[] {
  const str = normalizeString(value)
  if (!str) return []
  return str
    .split(/[,\s]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
}

function formatAdrClassesText(value: unknown): string {
  if (!Array.isArray(value)) return ''
  return value
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    .join(', ')
}

export function ParticipantProfileSelectField({
  value,
  onChange,
  labels,
}: {
  value?: string
  onChange: (value: string | undefined) => void
  labels: ParticipantSelectLabels
}) {
  const [options, setOptions] = React.useState<ParticipantOption[]>([])
  const [loading, setLoading] = React.useState(true)

  const loadOptions = React.useCallback(async () => {
    setLoading(true)
    try {
      const payload = await readApiResultOrThrow<{ items?: Array<Record<string, unknown>> }>(
        '/api/participants?pageSize=100',
        undefined,
        { errorMessage: labels.errorLoad },
      )
      const items = Array.isArray(payload?.items) ? payload.items : []
      const normalized: ParticipantOption[] = []
      for (const raw of items) {
        if (!raw || typeof raw !== 'object') continue
        const id = (raw as Record<string, unknown>).id
        if (typeof id !== 'string' || id.length === 0) continue
        const displayName =
          typeof (raw as Record<string, unknown>).customerDisplayName === 'string'
            ? ((raw as Record<string, unknown>).customerDisplayName as string).trim()
            : ''
        const participantType =
          typeof (raw as Record<string, unknown>).participantType === 'string'
            ? ((raw as Record<string, unknown>).participantType as string)
            : ''
        const label =
          displayName.length > 0
            ? participantType
              ? `${displayName} (${participantType})`
              : displayName
            : id
        normalized.push({ value: id, label })
      }
      normalized.sort((left, right) =>
        left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
      )
      setOptions(normalized)
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : labels.errorLoad
      flash(message, 'error')
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [labels.errorLoad])

  React.useEffect(() => {
    void loadOptions()
  }, [loadOptions])

  return (
    <div className="space-y-2">
      <select
        className="h-9 w-full rounded border px-3 text-sm"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
        disabled={loading}
      >
        <option value="">{labels.placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {loading ? <div className="text-xs text-muted-foreground">{labels.loadingLabel}</div> : null}
      {!loading && options.length === 0 ? (
        <div className="text-xs text-muted-foreground">{labels.emptyLabel}</div>
      ) : null}
    </div>
  )
}

export function createComplianceDocumentFormSchema() {
  return z.object({
    participantProfileId: z.string().uuid(),
    documentType: z.enum(complianceDocumentTypeValues),
    documentNumber: z.string().trim().min(1).max(100),
    documentName: z.string().trim().max(300).optional(),
    holderType: z.enum(holderTypeValues),
    issuerName: z.string().trim().max(300).optional(),
    issuerCountry: z.string().trim().max(3).optional(),
    issuedDate: z.string().optional(),
    validFrom: z.string().min(1),
    validUntil: z.string().optional(),
    isIndefinite: z.boolean().default(false),
    reviewIntervalMonths: z.number().int().positive().optional(),
    nextReviewDate: z.string().optional(),
    insuredSum: z.number().nonnegative().optional(),
    insuredSumCurrency: z.string().trim().max(3).optional(),
    insuredSumPer: z.enum(insuredSumPerValues).optional(),
    territorialScope: z.enum(territorialScopeValues).optional(),
    insurerName: z.string().trim().max(300).optional(),
    insurerPolicyNumber: z.string().trim().max(100).optional(),
    deductibleAmount: z.number().nonnegative().optional(),
    deductibleCurrency: z.string().trim().max(3).optional(),
    adrClassesText: z.string().trim().max(500).optional(),
    dgsaAdvisorName: z.string().trim().max(200).optional(),
    dgsaCertificateNumber: z.string().trim().max(100).optional(),
    alertSuppressed: z.boolean().default(false),
    notes: z.string().trim().max(4000).optional(),
  })
}

export function createComplianceDocumentFormFields(
  t: (key: string, fallbackOrParams?: string | Record<string, string | number>, params?: Record<string, string | number>) => string,
  options: { lockedParticipantId?: string | null } = {},
): CrudField[] {
  const documentTypeOptions = complianceDocumentTypeValues.map((value) => ({
    value,
    label: t(`compliance.document_type.${value}`, value),
  }))
  const holderTypeOptions = holderTypeValues.map((value) => ({
    value,
    label: t(`compliance.holder_type.${value}`, value),
  }))
  const insuredSumPerOptions = insuredSumPerValues.map((value) => ({
    value,
    label: t(`compliance.insured_sum_per.${value}`, value),
  }))
  const territorialScopeOptions = territorialScopeValues.map((value) => ({
    value,
    label: t(`compliance.territorial_scope.${value}`, value),
  }))

  return [
    {
      id: 'participantProfileId',
      label: t('compliance.form.participantProfile', 'Participant profile'),
      type: 'custom',
      layout: 'full',
      disabled: Boolean(options.lockedParticipantId),
      component: ({ value, setValue }: CrudCustomFieldRenderProps) => (
        <ParticipantProfileSelectField
          value={typeof value === 'string' ? value : undefined}
          onChange={(next) => setValue(next)}
          labels={{
            placeholder: t('compliance.form.participantProfilePlaceholder', 'Select participant'),
            loadingLabel: t('compliance.form.participantProfileLoading', 'Loading participants…'),
            emptyLabel: t('compliance.form.participantProfileEmpty', 'No participant profiles available.'),
            errorLoad: t('compliance.form.participantProfileLoadError', 'Failed to load participants.'),
          }}
        />
      ),
    },
    {
      id: 'documentType',
      label: t('compliance.fields.documentType', 'Document type'),
      type: 'select',
      required: true,
      layout: 'half',
      options: documentTypeOptions,
    },
    {
      id: 'holderType',
      label: t('compliance.fields.holderType', 'Holder type'),
      type: 'select',
      required: true,
      layout: 'half',
      options: holderTypeOptions,
    },
    {
      id: 'documentNumber',
      label: t('compliance.fields.documentNumber', 'Document number'),
      type: 'text',
      required: true,
      layout: 'half',
    },
    {
      id: 'documentName',
      label: t('compliance.fields.documentName', 'Document name'),
      type: 'text',
      layout: 'half',
    },
    {
      id: 'issuerName',
      label: t('compliance.fields.issuerName', 'Issuer'),
      type: 'text',
      layout: 'half',
    },
    {
      id: 'issuerCountry',
      label: t('compliance.fields.issuerCountry', 'Issuer country'),
      type: 'text',
      layout: 'half',
    },
    {
      id: 'issuedDate',
      label: t('compliance.fields.issuedDate', 'Issued date'),
      type: 'date',
      layout: 'third',
    },
    {
      id: 'validFrom',
      label: t('compliance.fields.validFrom', 'Valid from'),
      type: 'date',
      required: true,
      layout: 'third',
    },
    {
      id: 'validUntil',
      label: t('compliance.fields.validUntil', 'Valid until'),
      type: 'date',
      layout: 'third',
    },
    {
      id: 'isIndefinite',
      label: t('compliance.fields.isIndefinite', 'Indefinite validity'),
      type: 'checkbox',
      layout: 'half',
    },
    {
      id: 'reviewIntervalMonths',
      label: t('compliance.fields.reviewIntervalMonths', 'Review interval (months)'),
      type: 'number',
      layout: 'half',
    },
    {
      id: 'nextReviewDate',
      label: t('compliance.fields.nextReviewDate', 'Next review date'),
      type: 'date',
      layout: 'half',
    },
    {
      id: 'insuredSum',
      label: t('compliance.fields.insuredSum', 'Insured sum'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'insuredSumCurrency',
      label: t('compliance.fields.insuredSumCurrency', 'Insured sum currency'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'insuredSumPer',
      label: t('compliance.fields.insuredSumPer', 'Insured sum per'),
      type: 'select',
      layout: 'third',
      options: [
        { value: '', label: t('compliance.form.unset', '—') },
        ...insuredSumPerOptions,
      ],
    },
    {
      id: 'territorialScope',
      label: t('compliance.fields.territorialScope', 'Territorial scope'),
      type: 'select',
      layout: 'third',
      options: [
        { value: '', label: t('compliance.form.unset', '—') },
        ...territorialScopeOptions,
      ],
    },
    {
      id: 'insurerName',
      label: t('compliance.fields.insurerName', 'Insurer'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'insurerPolicyNumber',
      label: t('compliance.fields.insurerPolicyNumber', 'Insurer policy number'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'deductibleAmount',
      label: t('compliance.fields.deductibleAmount', 'Deductible amount'),
      type: 'number',
      layout: 'third',
    },
    {
      id: 'deductibleCurrency',
      label: t('compliance.fields.deductibleCurrency', 'Deductible currency'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'adrClassesText',
      label: t('compliance.fields.adrClasses', 'ADR classes'),
      type: 'text',
      layout: 'third',
      placeholder: t('compliance.form.adrClassesPlaceholder', 'e.g. 1.1D, 3, 6.1'),
    },
    {
      id: 'dgsaAdvisorName',
      label: t('compliance.fields.dgsaAdvisorName', 'DGSA advisor'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'dgsaCertificateNumber',
      label: t('compliance.fields.dgsaCertificateNumber', 'DGSA certificate number'),
      type: 'text',
      layout: 'third',
    },
    {
      id: 'alertSuppressed',
      label: t('compliance.fields.alertSuppressed', 'Suppress expiry alerts'),
      type: 'checkbox',
      layout: 'full',
    },
    {
      id: 'notes',
      label: t('compliance.fields.notes', 'Notes'),
      type: 'textarea',
      layout: 'full',
    },
  ]
}

export function createComplianceDocumentFormGroups(
  t: (key: string, fallbackOrParams?: string | Record<string, string | number>, params?: Record<string, string | number>) => string,
): CrudFormGroup[] {
  return [
    {
      id: 'compliance_document_identity',
      title: t('compliance.form.groups.identity.title', 'Document identity'),
      description: t(
        'compliance.form.groups.identity.description',
        'Link the document to a freight participant profile and set its core classification.',
      ),
      column: 2,
      fields: ['participantProfileId', 'documentType', 'holderType', 'documentNumber', 'documentName', 'issuerName', 'issuerCountry'],
    },
    {
      id: 'compliance_document_validity',
      title: t('compliance.form.groups.validity.title', 'Validity and review'),
      description: t(
        'compliance.form.groups.validity.description',
        'Capture issue and expiry dates, indefinite validity, and review cadence.',
      ),
      column: 2,
      fields: ['issuedDate', 'validFrom', 'validUntil', 'isIndefinite', 'reviewIntervalMonths', 'nextReviewDate'],
    },
    {
      id: 'compliance_document_insurance',
      title: t('compliance.form.groups.insurance.title', 'Insurance details (optional)'),
      description: t(
        'compliance.form.groups.insurance.description',
        'For insurance policies, record the insured sum, scope, insurer, and deductible.',
      ),
      column: 2,
      fields: [
        'insuredSum',
        'insuredSumCurrency',
        'insuredSumPer',
        'territorialScope',
        'insurerName',
        'insurerPolicyNumber',
        'deductibleAmount',
        'deductibleCurrency',
      ],
    },
    {
      id: 'compliance_document_adr',
      title: t('compliance.form.groups.adr.title', 'ADR / dangerous goods (optional)'),
      description: t(
        'compliance.form.groups.adr.description',
        'For ADR certificates, record the allowed classes and the DGSA advisor.',
      ),
      column: 2,
      fields: ['adrClassesText', 'dgsaAdvisorName', 'dgsaCertificateNumber'],
    },
    {
      id: 'compliance_document_notes',
      title: t('compliance.form.groups.notes.title', 'Alerts and notes'),
      column: 1,
      fields: ['alertSuppressed', 'notes'],
    },
    {
      id: 'compliance_document_custom_fields',
      title: t('compliance.form.groups.customFields.title', 'Custom fields'),
      kind: 'customFields',
    },
  ]
}

export function createComplianceDocumentInitialValues(
  participantProfileId?: string | null,
): ComplianceDocumentFormValues {
  return {
    participantProfileId: participantProfileId ?? '',
    documentType: 'ROAD_LICENSE_DOMESTIC',
    documentNumber: '',
    holderType: 'COMPANY',
    validFrom: '',
    isIndefinite: false,
    alertSuppressed: false,
  }
}

export function buildComplianceDocumentCreatePayload(values: ComplianceDocumentFormValues): Record<string, unknown> {
  const adrClasses = parseAdrClassesText(values.adrClassesText)
  const payload: Record<string, unknown> = {
    participantProfileId: values.participantProfileId,
    documentType: values.documentType,
    documentNumber: values.documentNumber,
    holderType: values.holderType,
    validFrom: normalizeDateInput(values.validFrom),
    isIndefinite: values.isIndefinite === true,
    alertSuppressed: values.alertSuppressed === true,
  }

  const optionalFields: Array<[string, unknown]> = [
    ['documentName', normalizeString(values.documentName)],
    ['issuerName', normalizeString(values.issuerName)],
    ['issuerCountry', normalizeString(values.issuerCountry)],
    ['issuedDate', normalizeDateInput(values.issuedDate)],
    ['validUntil', normalizeDateInput(values.validUntil)],
    ['reviewIntervalMonths', normalizeOptionalNumber(values.reviewIntervalMonths)],
    ['nextReviewDate', normalizeDateInput(values.nextReviewDate)],
    ['insuredSum', normalizeOptionalNumber(values.insuredSum)],
    ['insuredSumCurrency', normalizeString(values.insuredSumCurrency)],
    ['insuredSumPer', normalizeString(values.insuredSumPer)],
    ['territorialScope', normalizeString(values.territorialScope)],
    ['insurerName', normalizeString(values.insurerName)],
    ['insurerPolicyNumber', normalizeString(values.insurerPolicyNumber)],
    ['deductibleAmount', normalizeOptionalNumber(values.deductibleAmount)],
    ['deductibleCurrency', normalizeString(values.deductibleCurrency)],
    ['dgsaAdvisorName', normalizeString(values.dgsaAdvisorName)],
    ['dgsaCertificateNumber', normalizeString(values.dgsaCertificateNumber)],
    ['notes', normalizeString(values.notes)],
  ]

  optionalFields.forEach(([key, value]) => {
    if (value !== undefined) payload[key] = value
  })

  if (adrClasses.length > 0) {
    payload.adrClasses = adrClasses
  }

  return payload
}

export function buildComplianceDocumentUpdatePayload(
  id: string,
  values: ComplianceDocumentFormValues,
): Record<string, unknown> {
  const adrClasses = parseAdrClassesText(values.adrClassesText)
  return {
    id,
    documentType: values.documentType,
    documentNumber: values.documentNumber,
    documentName: normalizeNullableString(values.documentName),
    holderType: values.holderType,
    issuerName: normalizeNullableString(values.issuerName),
    issuerCountry: normalizeNullableString(values.issuerCountry),
    issuedDate: normalizeDateInput(values.issuedDate) ?? null,
    validFrom: normalizeDateInput(values.validFrom),
    validUntil: normalizeDateInput(values.validUntil) ?? null,
    isIndefinite: values.isIndefinite === true,
    reviewIntervalMonths: normalizeOptionalNumber(values.reviewIntervalMonths) ?? null,
    nextReviewDate: normalizeDateInput(values.nextReviewDate) ?? null,
    insuredSum: normalizeNullableNumber(values.insuredSum),
    insuredSumCurrency: normalizeNullableString(values.insuredSumCurrency),
    insuredSumPer: normalizeNullableString(values.insuredSumPer),
    territorialScope: normalizeNullableString(values.territorialScope),
    insurerName: normalizeNullableString(values.insurerName),
    insurerPolicyNumber: normalizeNullableString(values.insurerPolicyNumber),
    deductibleAmount: normalizeNullableNumber(values.deductibleAmount),
    deductibleCurrency: normalizeNullableString(values.deductibleCurrency),
    adrClasses,
    dgsaAdvisorName: normalizeNullableString(values.dgsaAdvisorName),
    dgsaCertificateNumber: normalizeNullableString(values.dgsaCertificateNumber),
    alertSuppressed: values.alertSuppressed === true,
    notes: normalizeNullableString(values.notes),
  }
}

export function mapComplianceDocumentRowToFormValues(
  row: Record<string, unknown>,
): ComplianceDocumentFormValues {
  return {
    participantProfileId: normalizeString(row.participant_profile_id) ?? '',
    documentType:
      (normalizeString(row.document_type) as ComplianceDocumentType | undefined) ?? 'ROAD_LICENSE_DOMESTIC',
    documentNumber: normalizeString(row.document_number) ?? '',
    documentName: normalizeString(row.document_name),
    holderType: (normalizeString(row.holder_type) as HolderType | undefined) ?? 'COMPANY',
    issuerName: normalizeString(row.issuer_name),
    issuerCountry: normalizeString(row.issuer_country),
    issuedDate: normalizeDateIso(row.issued_date),
    validFrom: normalizeDateIso(row.valid_from) ?? '',
    validUntil: normalizeDateIso(row.valid_until),
    isIndefinite: row.is_indefinite === true,
    reviewIntervalMonths: normalizeOptionalNumber(row.review_interval_months),
    nextReviewDate: normalizeDateIso(row.next_review_date),
    insuredSum: normalizeOptionalNumber(row.insured_sum),
    insuredSumCurrency: normalizeString(row.insured_sum_currency),
    insuredSumPer: normalizeString(row.insured_sum_per) as InsuredSumPer | undefined,
    territorialScope: normalizeString(row.territorial_scope) as TerritorialScope | undefined,
    insurerName: normalizeString(row.insurer_name),
    insurerPolicyNumber: normalizeString(row.insurer_policy_number),
    deductibleAmount: normalizeOptionalNumber(row.deductible_amount),
    deductibleCurrency: normalizeString(row.deductible_currency),
    adrClassesText: formatAdrClassesText(row.adr_classes),
    dgsaAdvisorName: normalizeString(row.dgsa_advisor_name),
    dgsaCertificateNumber: normalizeString(row.dgsa_certificate_number),
    alertSuppressed: row.alert_suppressed === true,
    notes: normalizeString(row.notes),
  }
}
